import { t, useLocale } from "../lib/i18n";
import { type FormEvent, useEffect, useRef, useState } from "react";
import {
  clearConsoleConnections,
  CONNECTIONS_CHANGED_EVENT,
  getConsoleConnections,
  getConnectionStorageError,
  setConsoleConnections,
} from "../lib/api";

import { AppearanceSettings } from "./AppearanceSettings";
import { testControlConnection, validateConnectionBase } from "../lib/connection-test";
import type { ChatCatalog } from "../lib/chat-api";
import { newMessageId, useSavedState } from "../lib/workbench-storage";
import { useDialogFocus } from "./useDialogFocus";

interface Profile { id: string; name: string; controlBase: string; traceBase: string }

interface ConnectionSettingsProps {
  open: boolean;
  onClose(): void;
}

/** Keep settings outside account-scoped routes so a partial Clear failure
 * remains visible after the old account's cache and forms are retired. */
export function ConnectionSettingsHost() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener("swarm-open-connection-settings", show);
    return () => window.removeEventListener("swarm-open-connection-settings", show);
  }, []);
  function close() {
    setOpen(false);
    requestAnimationFrame(() => document.getElementById("console-connections")?.focus());
  }
  return <ConnectionSettings open={open} onClose={close} />;
}

export function ConnectionSettings({
  open,
  onClose,
}: ConnectionSettingsProps) {
  useLocale();
  const [values, setValues] = useState(getConsoleConnections);
  const [error, setError] = useState("");
  const [testing, setTesting] = useState(false);
  const [tested, setTested] = useState<ChatCatalog | null>(null);
  const testController = useRef<AbortController | null>(null);
  const testGeneration = useRef(0);
  const [rawProfiles, saveProfiles, profilesSaved] = useSavedState<Profile[]>("swarm.workbench.connections", []);
  const profiles = Array.isArray(rawProfiles) ? rawProfiles.filter(item => item && typeof item.id === "string" && typeof item.name === "string" && typeof item.controlBase === "string" && typeof item.traceBase === "string") : [];
  const [profileName, setProfileName] = useState("");
  const [selectedProfile, setSelectedProfile] = useState("");
  useEffect(() => {
    testGeneration.current++; testController.current?.abort(); setTesting(false); setTested(null);
  }, [values, open]);
  useEffect(() => () => { testGeneration.current++; testController.current?.abort(); }, []);

  useEffect(() => {
    if (open) { setValues(getConsoleConnections()); setError(""); }
  }, [open]);
  const [connectionRevision, setConnectionRevision] = useState(0);
  useEffect(() => {
    const changed = () => { setValues(getConsoleConnections()); setConnectionRevision(value => value + 1); };
    window.addEventListener(CONNECTIONS_CHANGED_EVENT, changed);
    return () => window.removeEventListener(CONNECTIONS_CHANGED_EVENT, changed);
  }, []);

  const dialogRef = useRef<HTMLElement>(null);
  useDialogFocus(open, dialogRef, onClose, connectionRevision);

  if (!open) return null;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      validateConnectionBase(values.controlBase);
      if (values.traceBase) validateConnectionBase(values.traceBase);
      setConsoleConnections(values); onClose();
    } catch (error) { setError((error as Error).message || "Could not save connection settings."); }
  }

  function clear() {
    setError("");
    try { clearConsoleConnections(); setValues(getConsoleConnections()); }
    catch (error) { setValues(getConsoleConnections()); setError((error as Error).message || "Browser storage is unavailable. Could not clear saved connection settings."); }
  }

  async function testConnection() {
    testController.current?.abort();
    const controller = new AbortController(); testController.current = controller;
    const generation = ++testGeneration.current;
    const timeout = window.setTimeout(() => controller.abort(), 10_000);
    setTesting(true); setTested(null); setError("");
    try { const result = await testControlConnection(values, controller.signal); if (generation === testGeneration.current) setTested(result); }
    catch (error) { if (generation === testGeneration.current) setError(controller.signal.aborted ? "Connection test timed out after 10 seconds." : (error as Error).message); }
    finally { clearTimeout(timeout); if (generation === testGeneration.current) setTesting(false); }
  }

  return (
    <div className="dialog-backdrop" onMouseDown={onClose}>
      <section
        ref={dialogRef}
        tabIndex={-1}
        className="connection-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="connections-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="dialog-heading">
          <div>
            <p className="eyebrow">{t("Operator settings")}</p>
            <h2 id="connections-title">{t("Connections")}</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t("Close")}>
            ×
          </button>
        </div>

        <form onSubmit={submit} className="connection-form">
          {getConnectionStorageError() && <p role="alert" className="inline-error">{t(getConnectionStorageError())}</p>}
          <ol className="connection-steps"><li>{t("Run swarm api on your development machine.")}</li><li>{t("Enter its browser-reachable control URL and token below.")}</li><li>{t("Test authentication, save, then choose a recipe and machine.")}</li></ol>
          <section className="connection-profiles" aria-label={t("Connection profiles")}><label>{t("Connection profile")}<select aria-label={t("Connection profile")} value={selectedProfile} onChange={event => {
            setSelectedProfile(event.target.value); const profile = profiles.find(item => item.id === event.target.value);
            if (profile) { setProfileName(profile.name); setValues({ ...values, controlBase: profile.controlBase, traceBase: profile.traceBase, controlToken: "", traceToken: "" }); }
          }}><option value="">{t("Current / unsaved")}</option>{profiles.map(profile => <option key={profile.id} value={profile.id}>{profile.name}</option>)}</select></label>
          <label>{t("Profile name")}<input aria-label={t("Profile name")} maxLength={80} value={profileName} onChange={event => setProfileName(event.target.value)} placeholder={t("Development machine")} /></label>
          <button type="button" className="text-action" disabled={!profileName.trim()} onClick={() => {
            try { const controlBase = validateConnectionBase(values.controlBase); const traceBase = values.traceBase ? validateConnectionBase(values.traceBase) : "";
              const id = selectedProfile || newMessageId(); saveProfiles([...profiles.filter(profile => profile.id !== id), { id, name: profileName.trim(), controlBase, traceBase }]); setSelectedProfile(id);
            } catch (error) { setError((error as Error).message); }
          }}>{t("Save address profile")}</button>
          {selectedProfile && <button type="button" className="text-action" onClick={() => { saveProfiles(profiles.filter(profile => profile.id !== selectedProfile)); setSelectedProfile(""); }}>{t("Delete profile")}</button>}
          <p className="panel-note">{t("Profiles store names and addresses only. Selecting a profile clears both tokens; credentials are never copied to another server.")}</p>
          {!profilesSaved && <p role="alert" className="inline-error">{t("Could not save profiles in browser storage.")}</p>}
          </section>
          <ConnectionPair
            index="01"
            title={t("Agent control")}
            description="Mutable swarm-api endpoint used to start and continue sessions."
            base={values.controlBase}
            token={values.controlToken}
            onBase={(controlBase) => setValues({ ...values, controlBase })}
            onToken={(controlToken) => setValues({ ...values, controlToken })}
          />
          <div className="connection-test"><button type="button" className="button" disabled={testing} onClick={() => void testConnection()}>{testing ? t("Testing connection…") : t("Test control connection")}</button>
            {tested && <div role="status"><strong>{t("Authenticated · ")}{tested.agents.length} {t("recipes · ")}{tested.workers.length} {t("machines")}</strong><p className="panel-note">{t("Catalog checks passed. This does not verify provider login or start a model request; adapter readiness is checked when starting a session.")}</p>{(!tested.agents.length || !tested.workers.length) && <p className="result-warning">{t("Configure at least one recipe and worker on the control server.")}</p>}</div>}
          </div>
          {error && <p role="alert" className="inline-error">{t(error)}</p>}
          <details className="advanced-connections"><summary>{t("Advanced · trace archive and separate read token")}</summary>
          <ConnectionPair
            index="02"
            title={t("Trace archive")}
            description="Read-only swarm-trace-server used by the existing task and run views."
            base={values.traceBase}
            token={values.traceToken}
            onBase={(traceBase) => setValues({ ...values, traceBase })}
            onToken={(traceToken) => setValues({ ...values, traceToken })}
          />
          </details>
          <AppearanceSettings />
          <p className="connection-footnote">
            {t("Saved tokens are kept in this browser’s local storage, not encrypted. Do not use a shared browser profile. Tokens are never embedded in the frontend bundle or URL.")}</p>
          <p className="panel-note">{t("Connections are saved atomically. Reload older tabs after upgrading. Legacy values are ignored after the first save; Clear saved values also removes legacy credentials.")}</p>
          <div className="dialog-actions">
            <button className="button button--quiet" type="button" onClick={clear}>
              {t("Clear saved values")}</button>
            <button className="button button--primary" type="submit">
              {t("Save connections")}</button>
          </div>
        </form>
      </section>
    </div>
  );
}

function ConnectionPair({
  index,
  title,
  description,
  base,
  token,
  onBase,
  onToken,
}: {
  index: string;
  title: string;
  description: string;
  base: string;
  token: string;
  onBase(value: string): void;
  onToken(value: string): void;
}) {
  useLocale();
  return (
    <fieldset className="connection-block">
      <legend>
        <span>{index}</span> {title}
      </legend>
      <p>{t(description)}</p>
      <label>
        {t("API base URL")}<input
          type="text"
          inputMode="url"
          placeholder="/api or https://control.example.com"
          value={base}
          onChange={(event) => onBase(event.target.value)}
          spellCheck={false}
        />
      </label>
      <label>
        {t("Bearer token")}<input
          type="password"
          placeholder={t("Stored locally")}
          value={token}
          onChange={(event) => onToken(event.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
      </label>
    </fieldset>
  );
}
